import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import type { Member } from '@/api/b2b'
import { adminDiagnosticsApi, type DiagnosticAssignmentRow, type QuizSetSummary } from '@/api/adminDiagnostics'
import { ModalShell } from './ModalShell'

function displayAiqSetName(set: QuizSetSummary): string {
  const value = set.levelValue ?? ''
  if (value === '8') return 'A 세트 (7~8세)'
  if (value === '9') return 'B 세트 (9~10세)'
  if (value === '11') return 'C 세트 (11세 이상)'
  return set.name
}

function displayLevelSetName(set: QuizSetSummary): string {
  const base = set.name
    .replace(/\s*레벨\s*테스트\s*/g, ' ')
    .replace(/\s*레벨테스트\s*/g, ' ')
    .replace(/\s*세트\s*/g, ' ')
    .trim()
  return base ? `${base} 세트` : set.name
}

export function AssignModal({
  student, ageSets, gradeSets, onClose, onDone,
}: {
  student: Member
  ageSets: QuizSetSummary[]
  gradeSets: QuizSetSummary[]
  onClose: () => void
  onDone: (assignment?: DiagnosticAssignmentRow) => void
}) {
  const { t } = useTranslation('admin')
  const [aiqSetId, setAiqSetId] = useState<number | ''>('')
  const [levelSetId, setLevelSetId] = useState<number | ''>('')

  useQuery({
    queryKey: ['diagnostic-recommend', student.userId],
    queryFn: async () => {
      const res = await adminDiagnosticsApi.recommend(student.userId)
      const r = res.data.data
      if (r.aiqSet && aiqSetId === '') setAiqSetId(r.aiqSet.id)
      if (r.levelSet && levelSetId === '') setLevelSetId(r.levelSet.id)
      return r
    },
  })

  const assign = useMutation({
    mutationFn: () =>
      adminDiagnosticsApi.assign({
        userId: student.userId,
        aiqQuizSetId: Number(aiqSetId),
        levelQuizSetId: Number(levelSetId),
      }),
    onSuccess: (res) => onDone(res.data.data),
  })

  const canSubmit = aiqSetId !== '' && levelSetId !== ''

  return (
    <ModalShell title={t('diagnostic.assignModal.title', { name: student.fullName })} onClose={onClose}>
      <label className="block mb-3">
        <span className="text-sm font-medium text-gray-700 mb-1 block">{t('diagnostic.assignModal.aiqSetTitle')}</span>
        <select value={aiqSetId} onChange={(e) => setAiqSetId(Number(e.target.value) || '')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">{t('diagnostic.assignModal.selectPlaceholder')}</option>
          {ageSets.map((s) => <option key={s.id} value={s.id}>{displayAiqSetName(s)}</option>)}
        </select>
      </label>
      <label className="block mb-4">
        <span className="text-sm font-medium text-gray-700 mb-1 block">{t('diagnostic.assignModal.levelSetTitle')}</span>
        <select value={levelSetId} onChange={(e) => setLevelSetId(Number(e.target.value) || '')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">{t('diagnostic.assignModal.selectPlaceholder')}</option>
          {gradeSets.map((s) => <option key={s.id} value={s.id}>{displayLevelSetName(s)}</option>)}
        </select>
      </label>
      {assign.isError && <p className="text-sm text-red-600 mb-2">{t('diagnostic.assignModal.assignFailed')}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">{t('diagnostic.assignModal.close')}</button>
        <button type="button" disabled={!canSubmit || assign.isPending} onClick={() => assign.mutate()} className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm disabled:opacity-50">{t('diagnostic.assignModal.submit')}</button>
      </div>
    </ModalShell>
  )
}
